<template>
  <button
    v-if="product"
    class="wishlist-button wk-button"
    :class="`wishlist-button--${template}`"
    type="button"
    :data-product-handle="product?.handle"
    :data-variant-id="variant?.id"
    @click.prevent="handleClick"
  >
    <span class="wishlist-button__wishlist-icon">
      <span
        class="visually-hidden"
        v-text="$string('product.product_card.add_to_wishlist')"
      />

      <icon-wishlist />
    </span>

    <span
      class="wishlist-button__wishlist-icon-filled"
      :class="$class('hidden')"
    >
      <span
        class="visually-hidden"
        v-text="$string('product.product_card.remove_from_wishlist')"
      />

      <icon-wishlist-fill />
    </span>
  </button>
</template>

<script>
/**
 * Vue: Wishlist button (wishlist-button)
 * -----------------------------------------------------------------------------
 * Button to add/remove product from wishlist.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} template - `card`, `form`, or `product`.
 * @param {Boolean|Object} variant - Specified or default variant.
 *
 */
import { mapActions, mapState } from 'pinia';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import IconWishlist from '~icons/general/wishlist.svg';
import IconWishlistFill from '~icons/general/wishlist-fill.svg';

import { computeDiscountLogic } from '~/helpers/vue';

import { formatTrackingItem } from '~/format/format-tracking-item';
import useCartStore from '~stores/cart';

export default {
  name: 'WishlistButton',

  components: {
    IconWishlist,
    IconWishlistFill,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    template: {
      type: String,
      default: 'card',
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    location: {
      type: String,
      default: 'Collection Page',
    },
  },

  data() {
    return {
      key: 'cnvs-wishlist-data',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
  },

  methods: {
    ...mapActions(useCartStore, {
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    /**
     * Save product data to localStorage.
     * - Collection and badge data not available in wishlist.
     */
    handleClick() {
      const variantId = this.variant?.id;

      const details = {
        availableState: this.variant?.availableState || 'available',
        collectionIds: this.product?.collectionIds || [],
        productBadges: this.product?.badges || [],
        variantBadges: this.variant?.badges || [],
      };

      const data = {
        [variantId]: details,
      };

      const storage = localStorage.getItem(this.key);

      // Delay tracking to ensure data is updated.
      setTimeout(() => {
        const inWishlist = this.$el;
        if (inWishlist.dataset.inWishlist === 'true') {
          this.handleTrackingAddToWishlist();
        } else {
          this.handleTrackingRemoveFromWishlist();
        }
      }, 1000);

      /**
       * If storage doesn't exist then write directly.
       */
      if (!storage) {
        localStorage.setItem(this.key, JSON.stringify(data));
        return;
      }

      /**
       * If it does then update with new data.
       */
      const json = JSON.parse(storage);
      json[variantId] = details;
      localStorage.setItem(this.key, JSON.stringify(json));
    },

    /**
     * Tracking event add item to wishlist.
     */
    handleTrackingAddToWishlist() {
      const price = this.lineItem?.price || 0;
      const compareAtPrice = this.lineItem?.compare_at_price || 0;
      const discountLogic = computeDiscountLogic({
        discountPreview: false,
        onSale: compareAtPrice > price,
        product: this.product,
        variant: this.variant,
      });

      const campaignDiscount = this.getCampaignDiscount(
        discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        product: this.product,
        variant: this.variant,
        template: 'Wishlist Button',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: 1,
      });

      cnvs.EventBus.emit('dataLayer:add_to_wishlist', {
        event: 'add_to_wishlist',
        currency: cnvs.store.currency,
        location: this.location,
        items: [item],
      });
    },

    /**
     * Tracking event remove item from wishlist.
     */
    handleTrackingRemoveFromWishlist() {
      let itemSize = '-';
      if (typeof this.variant.options_with_values !== 'undefined') {
        itemSize =
          this.variant.options_with_values.find((option) => {
            if (
              cnvs.product.clothingSizeOptions.includes(
                option.name.toLowerCase(),
              ) ||
              cnvs.product.sizeOptions.includes(option.name.toLowerCase())
            ) {
              return true;
            }
            return false;
          })?.value || '-';
      } else if (typeof this.variant.selectedOptions !== 'undefined') {
        itemSize =
          this.variant.selectedOptions.find((option) => {
            if (
              cnvs.product.clothingSizeOptions.includes(
                option.name.toLowerCase(),
              ) ||
              cnvs.product.sizeOptions.includes(option.name.toLowerCase())
            ) {
              return true;
            }
            return false;
          })?.value || '-';
      }

      const item = {
        product_name: this.product.title,
        product_id: this.variant.sku,
        product_category: this.product.type || '-',
        product_size: itemSize,
        product_variant: this.variant.title,
        location: this.location,
      };
      cnvs.EventBus.emit('dataLayer:remove_from_wishlist', {
        event: 'remove_from_wishlist',
        currency: cnvs.store.currency,
        location: this.location,
        item,
      });
    },
  },
};
</script>

<style lang="scss">
@import './wishlist-button';
</style>
